import type { Session } from '@supabase/supabase-js'
import { createSessionDataClient, supabase } from '../auth.js'
import { generateId } from '../id.js'
import { incrementUsageOdometer } from './usageOdometer.js'
import { SYNC_PAYLOAD_FETCH_TIMEOUT_MS, SYNC_UPLOAD_NAME_ATTEMPTS, SYNC_CLEANUP_BATCH_LIMIT } from '../constants.js'
import type { SyncShelfSaveOutcome, SyncShelfSaveResult, SyncSpacePayloadLoadOutcome } from './syncEngine.js'
import type { RemoteSpaceMetaInput } from './databaseMemory.js'
import { SPACES_BUCKET, resolveSyncStoragePath, toSnapshotStoragePath } from './syncStoragePath.js'

const SPACES_TABLE = 'spaces'

export type SyncSpaceMeta = {
  path?: string | null
  space_id: string
  space_slug?: string | null
  space_ed?: string | null
  sync_at?: string | null
  size_bytes?: number | null
  deleted_at?: string | null
}

export function getSyncOwnerId(session: Session): string {
  const ownerId = session.user?.id?.trim()
  if (!ownerId) throw new Error('missing_owner')
  return ownerId
}

export function isSyncSpaceSlugConflictError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const record = error as { code?: unknown, message?: unknown, details?: unknown }
  const code = typeof record.code === 'string' ? record.code : ''
  const message = typeof record.message === 'string' ? record.message.toLowerCase() : ''
  const details = typeof record.details === 'string' ? record.details.toLowerCase() : ''
  if (code !== '23505') return false
  return message.includes('space_slug')
    || message.includes('spaces_owner_id_space_slug')
    || message.includes('spaces_owner_id_space_slug_live')
    || details.includes('space_slug')
    || details.includes('spaces_owner_id_space_slug')
    || details.includes('spaces_owner_id_space_slug_live')
}

export async function listSyncSpaces(ownerId: string, opts?: { includeDeleted?: boolean }): Promise<SyncSpaceMeta[]> {
  if (!supabase) throw new Error('supabase_unavailable')
  let query = supabase
    .from(SPACES_TABLE)
    .select('space_id, space_slug, space_ed, sync_at, size_bytes, deleted_at, path')
    .eq('owner_id', ownerId)
    .order('sync_at', { ascending: false })
  if (!opts?.includeDeleted) {
    query = query.is('deleted_at', null)
  }
  const { data, error } = await query
  if (error) throw error
  return (data ?? []) as SyncSpaceMeta[]
}

export async function loadSyncSpaceJson(ownerId: string, spaceId: string): Promise<SyncSpacePayloadLoadOutcome> {
  if (!supabase) return { status: 'retryable-failure' }
  const controller = new AbortController()
  let timeoutId: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<SyncSpacePayloadLoadOutcome>((resolve) => {
    timeoutId = globalThis.setTimeout(() => {
      controller.abort()
      resolve({ status: 'retryable-failure' })
    }, SYNC_PAYLOAD_FETCH_TIMEOUT_MS)
  })

  try {
    return await Promise.race([
      loadSyncSpaceJsonWithinTimeout(ownerId, spaceId, controller.signal),
      timeout
    ])
  } finally {
    if (timeoutId !== undefined) globalThis.clearTimeout(timeoutId)
  }
}

async function loadSyncSpaceJsonWithinTimeout(
  ownerId: string,
  spaceId: string,
  signal: AbortSignal
): Promise<SyncSpacePayloadLoadOutcome> {
  if (!supabase) return { status: 'retryable-failure' }
  try {
    const { data: meta, error: lookupError } = await supabase
      .from(SPACES_TABLE)
      .select('owner_id, space_id, space_slug, space_ed, sync_at, size_bytes, deleted_at, path')
      .eq('owner_id', ownerId)
      .eq('space_id', spaceId)
      .abortSignal(signal)
      .maybeSingle()
    if (lookupError) return { status: 'retryable-failure' }
    if (!meta) return { status: 'absent' }
    if (meta.deleted_at) return { status: 'deleted', meta }

    const pathname = resolveSyncStoragePath(ownerId, spaceId, meta.path)
    const { data: signed, error: signedError } = await supabase.storage
      .from(SPACES_BUCKET)
      .createSignedUrl(pathname, 30)
    if (signedError) {
      return isConfirmedSyncPayloadAbsenceError(signedError)
        ? { status: 'missing-payload', meta }
        : { status: 'retryable-failure' }
    }
    if (!signed?.signedUrl) return { status: 'retryable-failure' }

    const noStoreUrl = `${signed.signedUrl}${signed.signedUrl.includes('?') ? '&' : '?'}_ts=${Date.now()}`
    const res = await fetch(noStoreUrl, { cache: 'no-store', signal })
    if (res.status === 404) return { status: 'missing-payload', meta }
    if (!res.ok) {
      const responseError = await res.clone().json().catch(() => undefined)
      return isConfirmedSyncPayloadAbsenceError(responseError)
        ? { status: 'missing-payload', meta }
        : { status: 'retryable-failure' }
    }
    return { status: 'payload', payload: await res.text(), meta }
  } catch {
    return { status: 'retryable-failure' }
  }
}

export function isConfirmedSyncPayloadAbsenceError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const record = error as { status?: unknown, statusCode?: unknown }
  return record.status === 404
    || record.statusCode === 404
    || record.statusCode === '404'
}

// The token is captured once. Even if the SDK session changes while a request is
// pending, subsequent requests cannot silently act as the newly mounted owner.
export type SyncWriteContext = {
  session: Pick<Session, 'access_token' | 'user'>
  assertCurrent: () => Promise<void>
}

function writeAuthorization(ownerId: string, context: SyncWriteContext): string {
  if (context.session.user.id !== ownerId || !context.session.access_token) {
    throw new Error('sync_owner_mismatch')
  }
  return `Bearer ${context.session.access_token}`
}

async function withinSyncTimeout<T>(request: (signal: AbortSignal) => PromiseLike<T>): Promise<T> {
  const controller = new AbortController()
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race([
      request(controller.signal),
      new Promise<never>((_, reject) => {
        timer = globalThis.setTimeout(() => {
          controller.abort()
          reject(new Error('sync_request_timeout'))
        }, SYNC_PAYLOAD_FETCH_TIMEOUT_MS)
      })
    ])
  } finally {
    if (timer !== undefined) globalThis.clearTimeout(timer)
  }
}

function isSpaceRow(value: unknown, ownerId: string, spaceId: string): value is RemoteSpaceMetaInput {
  if (!value || typeof value !== 'object') return false
  const row = value as Record<string, unknown>
  return row.owner_id === ownerId && row.space_id === spaceId
    && typeof row.sync_at === 'string' && row.sync_at.length > 0
    && typeof row.space_ed === 'string' && row.space_ed.length > 0
}

function acknowledgement(row: RemoteSpaceMetaInput): SyncShelfSaveResult {
  return { syncAt: row.sync_at, spaceSlug: row.space_slug, spaceEd: row.space_ed, deletedAt: row.deleted_at }
}

function isUploadNameCollision(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const value = error as { status?: unknown, statusCode?: unknown, code?: unknown, message?: unknown }
  // The installed Storage SDK retains status/message, but drops the server code.
  // Standard uploads also document the older 400 Asset Already Exists response.
  const legacyCollision = (value.status === 400 || String(value.statusCode) === '400')
    && typeof value.message === 'string'
    && ['asset already exists', 'the resource already exists', 'resource already exists'].includes(value.message.toLowerCase())
  return legacyCollision || value.status === 409 || String(value.statusCode) === '409'
    || value.code === 'Duplicate' || value.code === 'ResourceAlreadyExists' || value.code === 'KeyAlreadyExists'
}

export async function saveSyncSpaceSnapshot(ownerId: string, payload: {
  spaceId: string
  spaceSlug: string
  spaceEd: string
  expectedPath: string | null
  json: string
}, context: SyncWriteContext): Promise<SyncShelfSaveOutcome> {
  const client = supabase
  if (!client) throw new Error('supabase_unavailable')
  const authorization = writeAuthorization(ownerId, context)
  const { spaceId, spaceEd, expectedPath, json } = payload
  const spaceSlug = payload.spaceSlug || spaceId
  if (expectedPath === undefined) throw new Error('missing_expected_path')
  if (expectedPath !== null) resolveSyncStoragePath(ownerId, spaceId, expectedPath)
  const blob = new Blob([json], { type: 'application/json' })
  const rpc = (name: string, args?: Record<string, unknown>) => client.rpc(name, args)
    .setHeader('Authorization', authorization)
  let pathname = ''
  for (let attempt = 0; attempt < SYNC_UPLOAD_NAME_ATTEMPTS; attempt += 1) {
    await context.assertCurrent()
    pathname = toSnapshotStoragePath(ownerId, spaceId, generateId())
    const upload = await withinSyncTimeout(() => client.storage.from(SPACES_BUCKET)
      .upload(pathname, blob, {
        upsert: false, contentType: 'application/json', cacheControl: '0',
        headers: { Authorization: authorization },
        metadata: { space_id: spaceId, expected_path: expectedPath, space_slug: spaceSlug, space_ed: spaceEd }
      }).then(result => {
        // A confirmed transfer counts even if acceptance fails, or this upload
        // finishes after our timeout. Metering stays attached to its original login.
        if (!result.error) void incrementUsageOdometer({
          syncPushCountDelta: 1, syncPushBytesDelta: blob.size
        }, { rpc: (name, args) => withinSyncTimeout(signal => rpc(name, args).abortSignal(signal)) }).catch(() => undefined)
        return result
      }))
    if (!upload.error) break
    if (!isUploadNameCollision(upload.error) || attempt + 1 === SYNC_UPLOAD_NAME_ATTEMPTS) throw upload.error
  }
  await context.assertCurrent()

  const accepted = (row: unknown): row is RemoteSpaceMetaInput => isSpaceRow(row, ownerId, spaceId)
    && !row.deleted_at && row.path === pathname && row.space_slug === spaceSlug
    && row.space_ed === spaceEd && row.size_bytes === blob.size
  const deleted = (row: unknown): row is RemoteSpaceMetaInput => isSpaceRow(row, ownerId, spaceId)
    && typeof row.deleted_at === 'string' && row.deleted_at.length > 0
  let reply: { status?: string, space?: unknown } | undefined
  try {
    const { data, error } = await withinSyncTimeout(signal => rpc('accept_space_upload', {
      p_space_id: spaceId, p_path: pathname, p_expected_path: expectedPath,
      p_space_slug: spaceSlug, p_space_ed: spaceEd
    }).abortSignal(signal))
    if (error) throw error
    reply = data
  } catch {
    // Receiving no answer is not rejection. Inspect the pointer once; never
    // rebase this candidate or issue a second handover without a fresh merge.
    await context.assertCurrent()
    const { data, error } = await withinSyncTimeout(signal => client.from(SPACES_TABLE)
      .select('owner_id, space_id, space_slug, space_ed, sync_at, size_bytes, deleted_at, path')
      .eq('owner_id', ownerId).eq('space_id', spaceId)
      .setHeader('Authorization', authorization).abortSignal(signal).maybeSingle())
    await context.assertCurrent()
    if (error) return { ok: false, reason: 'failed' }
    if (accepted(data)) return { ok: true, ...acknowledgement(data) }
    if (deleted(data)) return { ok: false, reason: 'deleted', meta: data }
    return { ok: false, reason: 'pointer-conflict' }
  }
  await context.assertCurrent()
  if (reply?.status === 'accepted' && accepted(reply.space)) return { ok: true, ...acknowledgement(reply.space) }
  if (reply?.status === 'deleted' && deleted(reply.space)) return { ok: false, reason: 'deleted', meta: reply.space }
  if (reply?.status === 'conflict') return { ok: false, reason: 'pointer-conflict' }
  if (reply?.status === 'slug-conflict') return { ok: false, reason: 'slug-conflict' }
  return { ok: false, reason: 'failed' }
}

export async function deleteSyncSpace(
  ownerId: string, spaceId: string, deletedAt: string, context: SyncWriteContext
): Promise<SyncShelfSaveResult> {
  const client = supabase
  if (!client) throw new Error('supabase_unavailable')
  const authorization = writeAuthorization(ownerId, context)
  await context.assertCurrent()
  const { data, error } = await withinSyncTimeout(signal => client.rpc('delete_space', {
    p_space_id: spaceId, p_deleted_at: deletedAt
  }).setHeader('Authorization', authorization).abortSignal(signal))
  await context.assertCurrent()
  if (error) throw error
  if (data?.status !== 'deleted' || !isSpaceRow(data.space, ownerId, spaceId)
    || typeof data.space.deleted_at !== 'string' || data.space.deleted_at < deletedAt
    || data.space.path !== null) throw new Error('remote_delete_unconfirmed')
  // The database recorded retirement. Byte removal belongs to receipt cleanup.
  return acknowledgement(data.space)
}


export type SyncCleanupResult = { pending: boolean, failed: boolean }

// One bounded batch, never a bucket scan. A receipt is the database's approval;
// filenames are never reused by the guarded handover after retirement.
export async function cleanupSyncSpaceFiles(ownerId: string, context: SyncWriteContext): Promise<SyncCleanupResult> {
  try {
    writeAuthorization(ownerId, context)
    return await withinSyncTimeout(async signal => {
      const assertCurrent = async () => {
        if (signal.aborted) throw new Error('sync_cleanup_cancelled')
        await context.assertCurrent()
        if (signal.aborted) throw new Error('sync_cleanup_cancelled')
      }
      await assertCurrent()
      const client = createSessionDataClient(context.session.access_token, signal)
      const { data, error } = await client.from('cleanup').select('owner_id, path')
        .eq('owner_id', ownerId).order('path').limit(SYNC_CLEANUP_BATCH_LIMIT)
      if (error) throw error
      if (!Array.isArray(data)) throw new Error('invalid_cleanup_receipts')
      if (!data.length) return { pending: false, failed: false }
      // Reject an unexpected batch before issuing any Storage mutation.
      if (data.length > SYNC_CLEANUP_BATCH_LIMIT) throw new Error('invalid_cleanup_batch')
      const paths = [...new Set(data.map(row => {
        if (row.owner_id !== ownerId || typeof row.path !== 'string' || !row.path) throw new Error('invalid_cleanup_receipt')
        return resolveSyncStoragePath(ownerId, '', row.path)
      }))]
      await assertCurrent()
      const bucket = client.storage.from(SPACES_BUCKET)
      const removed = await bucket.remove(paths)
      if (removed.error) throw removed.error
      const confirmed = new Set<string>()
      if (Array.isArray(removed.data)) {
        for (const file of removed.data) if (paths.includes(file.name)) confirmed.add(file.name)
      }
      // A competing cleaner may have removed some files already. A success with
      // an empty/partial response alone is not proof of absence.
      for (const path of paths) {
        if (confirmed.has(path)) continue
        await assertCurrent()
        const info = await bucket.info(path)
        if (isConfirmedSyncPayloadAbsenceError(info.error)) confirmed.add(path)
      }
      await assertCurrent()
      if (confirmed.size) {
        const cleared = await client.from('cleanup').delete().eq('owner_id', ownerId).in('path', [...confirmed])
        if (cleared.error) throw cleared.error
      }
      const failed = confirmed.size < paths.length
      return { pending: failed || data.length === SYNC_CLEANUP_BATCH_LIMIT, failed }
    })
  } catch {
    // Retaining a receipt is safe, including a timeout after the file was removed
    // or after receipt deletion committed. Another pass can confirm either case.
    return { pending: true, failed: true }
  }
}
